import apiService from "../../../../api/rest";
import { DateTime, Interval } from "luxon";
import errorLoggerAdapter from "@/adapters/errorLoggerAdapter";
import { SENTRY_ERRORS, OAPI_API_TYPE } from "@/utils";

export const fetchVenueVendors = async ({ commit, dispatch, rootGetters }, payload) => {
  try {
    const venueId = `${payload}`;
    const response = await apiService.fetchIWVenueVendors(venueId, {});

    dispatch("common/updateVendorTimeStamp", null, { root: true });

    commit("setVendorsList", response.data);

    return response.data;
  } catch (error) {
    errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_FETCH_VENDORS, "fetchVenueVendors", "fetchVenueVendors", {}, error);
    return Promise.reject(error);
  }
};

export const fetchVendorById = async ({ commit, dispatch, rootGetters }, { vendorId, updateActiveVendor = true }) => {
  try {
    const venueId = rootGetters["venue/getActiveVenue"];
    const response = await apiService.fetchIWVenueVendorById(venueId, vendorId);
    commit("updateVendor", response.data);
    // calling the bellow action will add "isOpen" value to the vendor has been updated.
    dispatch("vendor/detectClosedVendors", null, { root: true });
    dispatch("common/updateVendorTimeStamp", null, { root: true });

    if (updateActiveVendor) {
      const activeVendor = rootGetters["vendor/getVendorByID"](vendorId);
      commit("setActiveVendor", activeVendor);
    }

    return response.data;
  } catch (error) {
    errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_FETCH_VENDORS, "fetchVendorById", "fetchVendorById", {}, error);
    return Promise.reject(error);
  }
};

export const fetchVendorPickupTimes = async ({ commit, dispatch, rootGetters }, { vendorId }) => {
  try {
    const isOAPI = rootGetters["venueSettings/APIType"] === OAPI_API_TYPE;
    const isPickupTimeSelectionEnable = rootGetters["vendor/isPickupTimeSelectionEnableByVendorId"](vendorId);
    if (!isOAPI || !isPickupTimeSelectionEnable) return [];

    const venueId = rootGetters["venue/getActiveVenue"];
    const response = await apiService.fetchIWVenueVendorPickupTimes(venueId, vendorId);
    commit("updateVendorPickupTimes", { vendorId, pickupTimes: response.data });
    return response.data;
  } catch (error) {
    errorLoggerAdapter.createIssue(
      SENTRY_ERRORS.ERROR_FETCH_VENDORS,
      "fetchIWVenueVendorPickupTimes",
      "fetchIWVenueVendorPickupTimes",
      {},
      error
    );
    return Promise.reject(error);
  }
};

export const fetchLocationVendors = async ({ commit }, payload) => {
  try {
    const venueId = `${payload.venueId}`;
    const request = {
      locationSelection: {
        levelId: `${payload.levelId || ""}`,
        sectionId: `${payload.sectionId || ""}`,
        rowId: `${payload.rowId || ""}`,
        seatId: `${payload.seatId || ""}`
      }
    };
    const response = await apiService.fetchIWVenueVendors(venueId, request);

    const vendors = response.data || [];

    commit("delivery/setSeatSelection", payload, { root: true });
    commit("setVendorsList", vendors);
    return vendors;
  } catch (error) {
    errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_FETCH_VENDORS, "fetchLocationVendors", "fetchLocationVendors", {}, error);
    return Promise.reject(error);
  }
};

export const fetchDeepLinkVendors = async ({ dispatch, commit, rootGetters }, payload) => {
  try {
    const venueId = rootGetters["venue/getActiveVenue"];
    const request = {
      locationSelection: {
        levelName: `${payload.levelName || ""}`,
        sectionName: `${payload.sectionName || ""}`,
        rowName: `${payload.rowName || ""}`,
        seatName: `${payload.seatName || ""}`
      }
    };

    const response = await apiService.fetchIWDeepLinkVendors(venueId, request);
    const vendors = response.data.vendors || [];
    const { levels, sections, levelId, sectionId, rowId = undefined, seatId = undefined } = response.data.locationData;
    const seatSelection = {
      levelId,
      sectionId,
      rowId,
      seatId
    };

    commit("level/setLevelsList", levels, { root: true });
    commit("level/setSectionsList", sections, { root: true });
    commit("delivery/setSeatSelection", seatSelection, { root: true });
    commit("setVendorsList", vendors);
    dispatch("level/setActiveSeatSelection", seatSelection, { root: true });
    return vendors;
  } catch (error) {
    errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_FETCH_VENDORS, "fetchDeepLinkVendors", "fetchDeepLinkVendors", {}, error);
    return Promise.reject(error);
  }
};

export function fetchSubvendorData({ commit }, values) {
  return apiService.fetchSubvendorInfo(values.vendorId, values.venueId).then((data) => {
    commit("setSubvendorList", data);
    return data;
  });
}

let timerId;
export function startClosedVendorsCheck({ dispatch }, vendorList) {
  timerId = setInterval(() => dispatch("detectClosedVendors", vendorList), 60000);
}

export function stopClosedVendorsCheck() {
  clearInterval(timerId);
}

export function detectClosedVendors({ commit, getters, rootGetters }, payload) {
  const vendorList = payload || getters.getVendorsList;
  vendorList.forEach((vendor) => {
    if (!vendor?.id) return;

    if (vendor.enableHoursOfOperation !== 1) {
      return commit("setVendorStatus", { id: vendor.id, status: true }); // it's always open
    }

    const { timezone } = rootGetters["venue/getVenueInfo"];
    const currentTime = DateTime.now().setZone(timezone);
    const currentWeekDay = currentTime.toFormat("EEEE");
    const openHours = getters.getMappedOpenHoursOfVendor(vendor.openHours);

    if (!openHours) {
      // mark as open if openHours comes through with no value, null.
      return commit("setVendorStatus", { id: vendor.id, status: true });
    }

    // checks for hours for current day, if nothing for current day will set to closed.
    if (openHours[currentWeekDay]) {
      // checks hours for current day against the device time.
      const isOpen = openHours[currentWeekDay].some((timePeriod) => {
        return Interval.fromDateTimes(
          DateTime.fromISO(timePeriod.startTime).setZone(timezone),
          DateTime.fromISO(timePeriod.endTime).setZone(timezone)
        ).contains(currentTime);
      });

      // checks if it's currently open based on hours for that day.
      if (isOpen) return commit("setVendorStatus", { id: vendor.id, status: true });

      // not currently open
      return commit("setVendorStatus", { id: vendor.id, status: false });
    }

    // no open hours data for current weekday but open hours is enabled, sets to closed.
    return commit("setVendorStatus", { id: vendor.id, status: false });
  });
}
